/**
 * Home Dashboard Page
 *
 * Drives the core product loop: Practice → Skill practice → Marketplace
 *
 * Sections:
 *   A — Primary Actions (Start Practice / Skill practice / Marketplace)
 *   B — Marketplace Readiness (authority, assessments, profile checklist)
 *   C — Recent Performance (sessions, scores, trends)
 *   D — Next Best Step (rules-based recommendation)
 */

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  useGetMyGamificationOverviewQuery,
  type AuthorityLevelKey,
  type GamificationOverview,
} from '../store/api/gamificationApi';
import {
  useGetAssessmentStatsQuery,
  type UserSessionStats,
} from '../store/api/assessmentApi';
import { useListPracticeSessionsQuery } from '../store/api/practiceApi';
import { deriveNextStep } from '../store/api/onboardingApi';
import { useGetMyPublicProfileSettingsQuery } from '../store/api/portfolioApi';
import { useOnboardingPath } from '../hooks/useOnboardingPath';
import { useInView } from '../hooks/useInView';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

// ============================================
// CONSTANTS
// ============================================

const MARKETPLACE_AUTHORITY_THRESHOLD = 60;
/** Checklist detail while the lazily read public profile is in flight. */
const CHECKING = 'Checking…';

interface ReadinessState {
  meetsAuthorityThreshold: boolean;
  hasPassedAssessment: boolean;
  /**
   * RC19: the server says skill practice cannot be scored yet (no evaluator):
   * a pass is impossible, so it is neither a routing step nor a checklist item.
   */
  skillScoringUnavailable?: boolean;
  hasProfileBasics: boolean;
  hasFeaturedClip: boolean;
  hasPublicProfile: boolean;
  /** The public-profile settings could not be read: the profile steps are unknown. */
  profileUnavailable?: boolean;
}

interface ReadinessRoute {
  to: string;
  label: string;
  sublabel: string;
}

/**
 * Rules-based readiness routing. Returns the CTA target based on
 * what the user still needs to do, in priority order:
 *   1. authority threshold not met → /practice
 *   2. no passed assessment → /skills
 *   3. profile incomplete → /settings/public-profile
 *   4. no featured clip → /portfolio
 *   5. all met → /marketplace
 */
function computeReadinessRoute(state: ReadinessState): ReadinessRoute {
  if (!state.meetsAuthorityThreshold) {
    return { to: '/practice', label: 'Keep Practicing', sublabel: 'Build your performance score' };
  }
  if (!state.hasPassedAssessment && !state.skillScoringUnavailable) {
    return { to: '/skills', label: 'Skill practice', sublabel: 'Pass a scored skill run' };
  }
  // Unknown profile state is not "incomplete": no claim about it.
  if (state.profileUnavailable) {
    return { to: '/settings/public-profile', label: 'Check Your Profile', sublabel: 'Profile status is unavailable right now' };
  }
  if (!state.hasProfileBasics) {
    return { to: '/settings/public-profile', label: 'Complete Profile', sublabel: 'Add your name and headline' };
  }
  if (!state.hasFeaturedClip) {
    return { to: '/portfolio', label: 'Add an Example', sublabel: 'Feature how you perform' };
  }
  if (!state.hasPublicProfile) {
    return { to: '/settings/public-profile', label: 'Publish Profile', sublabel: 'Make yourself discoverable' };
  }
  return { to: '/marketplace', label: 'Get Discovered', sublabel: 'Show how you perform in real conversations' };
}

const LEVEL_COLORS: Record<AuthorityLevelKey, { bg: string; text: string; gradient: string }> = {
  novice: { bg: 'bg-gray-100', text: 'text-gray-700', gradient: 'from-gray-400 to-gray-500' },
  bronze: { bg: 'bg-amber-100', text: 'text-amber-700', gradient: 'from-amber-500 to-amber-600' },
  silver: { bg: 'bg-slate-200', text: 'text-slate-700', gradient: 'from-slate-400 to-slate-500' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', gradient: 'from-yellow-400 to-yellow-500' },
  platinum: { bg: 'bg-purple-100', text: 'text-purple-700', gradient: 'from-purple-500 to-indigo-600' },
};

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * Whether the user has completed any session, from the gamification overview.
 * The overview's totalSessions counts EVERY completed attempt — legacy practice
 * sessions and canonical conversation sessions (Written Chat / Live
 * Conversation), each once — so a coaching-only user is not told to "start
 * their first practice session". No overview = unknown, not none.
 */
export function sessionHistory(stats: { totalSessions?: number | null } | null | undefined): 'none' | 'some' | 'unknown' {
  const n = stats?.totalSessions;
  if (typeof n !== 'number' || !Number.isFinite(n)) return 'unknown';
  return n > 0 ? 'some' : 'none';
}

export function HomeDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Data sources — a fresh load makes 4 BeHeard requests (see
  // __tests__/dashboard-request-budget.test.tsx): onboarding (guard, with the
  // path included), gamification overview, assessment stats, latest session.
  const {
    data: gamification,
    isLoading: isLoadingGamification,
    isError: gamificationFailed,
    error: gamificationError,
    refetch: refetchGamification,
  } = useGetMyGamificationOverviewQuery();
  const {
    data: assessmentStats,
    isError: assessmentStatsFailed,
    error: assessmentStatsError,
    refetch: refetchAssessmentStats,
  } = useGetAssessmentStatsQuery();
  // Only the latest session is rendered (its score bars), so only it is read.
  const { data: recentSessions } = useListPracticeSessionsQuery({
    limit: 1,
    status: 'completed',
  });
  // From OnboardingGuard's GET /onboarding/me?include=path — no request here.
  const path = useOnboardingPath();
  // Derived from the path already loaded — no separate next-step request.
  const nextStepData = useMemo(
    () => (path === undefined ? undefined : deriveNextStep(path)),
    [path]
  );

  const authority = gamification?.authority;
  // "Passed at least one skill practice run" means a measured PASS — a finished test whose
  // result was a fail or unscored is not one (completedSessions counts those).
  const hasPassedAssessment = (assessmentStats?.passedSessions ?? 0) > 0;
  // RC19: no evaluator → skill runs are unscored practice; no pass is possible.
  const skillScoringUnavailable = assessmentStats?.scoringAvailable === false;
  const authorityScore = authority?.overallScoreAvg ?? null;
  const meetsAuthorityThreshold = authorityScore !== null && authorityScore >= MARKETPLACE_AUTHORITY_THRESHOLD;

  // Public-profile settings feed the readiness checklist's last three rows,
  // and the readiness route only once the first two rules pass. So they are
  // read lazily: when the readiness card nears the viewport, or right away
  // when routing actually depends on them. Until then the profile rows show
  // "Checking…" in place (same height — no layout jump).
  const [readinessCardRef, readinessCardInView] = useInView<HTMLDivElement>();
  const routingNeedsProfile = meetsAuthorityThreshold && (hasPassedAssessment || skillScoringUnavailable);
  const {
    data: publicProfile,
    isError: publicProfileFailed,
    error: publicProfileError,
    refetch: refetchPublicProfile,
  } = useGetMyPublicProfileSettingsQuery(undefined, {
    skip: !(readinessCardInView || routingNeedsProfile),
  });

  if (isLoadingGamification) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const stats = gamification?.stats;
  const history = sessionHistory(stats);
  // "First session" copy only when the server SAYS there are none; an
  // unavailable overview is unknown, never "no sessions".
  const hasAnySessions = history !== 'none';

  // Profile readiness — derived from real public profile settings API
  const hasPublicProfile = !!(
    publicProfile &&
    publicProfile.isPublic &&
    publicProfile.displayName &&
    publicProfile.headline
  );
  const hasProfileBasics = !!(
    publicProfile?.displayName &&
    publicProfile?.headline
  );
  const hasFeaturedClip = (publicProfile?.featuredClips?.length ?? 0) > 0;

  // Readiness routing — ordered priority
  const readiness = computeReadinessRoute({
    meetsAuthorityThreshold,
    hasPassedAssessment,
    skillScoringUnavailable,
    hasProfileBasics,
    hasFeaturedClip,
    hasPublicProfile,
    profileUnavailable: publicProfileFailed,
  });

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back{user?.firstName ? `, ${user.firstName}` : ''}
        </h1>
        <p className="mt-1 text-gray-500">
          Practice real conversations. Build your skills. Get discovered.
        </p>
      </div>

      {/* ========== SECTION A: PRIMARY ACTIONS ========== */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link
          to="/practice"
          className="group relative overflow-hidden rounded-xl border-2 border-primary-200 bg-gradient-to-br from-primary-50 to-primary-100 p-6 hover:border-primary-400 hover:shadow-lg transition-all"
        >
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 rounded-full bg-primary-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <MicIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Start Practice</h3>
              <p className="text-sm text-gray-600 mt-1">Practice real conversations with live feedback</p>
            </div>
          </div>
        </Link>

        <Link
          to="/skills"
          className="group relative overflow-hidden rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-6 hover:border-blue-400 hover:shadow-lg transition-all"
        >
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <ClipboardIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Skill practice</h3>
              <p className="text-sm text-gray-600 mt-1">Practice the skills; scoring isn't available yet</p>
            </div>
          </div>
        </Link>

        <Link
          to={readiness.to}
          className="group relative overflow-hidden rounded-xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-6 hover:border-green-400 hover:shadow-lg transition-all"
        >
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <MarketplaceIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">{readiness.label}</h3>
              <p className="text-sm text-gray-600 mt-1">{readiness.sublabel}</p>
            </div>
          </div>
        </Link>
      </div>

      {/* ========== SECTION D: NEXT BEST STEP ========== */}
      <NextBestStepCard
        hasAnySessions={hasAnySessions}
        readiness={readiness}
      />

      {/* ========== SECTION B: MARKETPLACE READINESS ========== */}
      <MarketplaceReadinessCard
        cardRef={readinessCardRef}
        profileKnown={publicProfile !== undefined || publicProfileFailed}
        profileUnavailable={publicProfileFailed}
        profileError={publicProfileError}
        onRetryProfile={() => void refetchPublicProfile()}
        authorityUnavailable={gamificationFailed}
        assessmentStatsUnavailable={assessmentStatsFailed}
        unavailableError={gamificationFailed ? gamificationError : assessmentStatsError}
        onRetry={() => {
          if (gamificationFailed) void refetchGamification();
          if (assessmentStatsFailed) void refetchAssessmentStats();
        }}
        authority={authority}
        assessmentStats={assessmentStats}
        hasPassedAssessment={hasPassedAssessment}
        meetsAuthorityThreshold={meetsAuthorityThreshold}
        hasProfileBasics={hasProfileBasics}
        hasFeaturedClip={hasFeaturedClip}
        hasPublicProfile={hasPublicProfile}
        readiness={readiness}
      />

      {/* ========== SECTION C: RECENT PERFORMANCE ========== */}
      <RecentPerformanceCard
        gamification={gamification}
        recentSessions={recentSessions?.data}
        hasAnySessions={hasAnySessions}
      />

      {/* Assessment path (secondary — collapsed into smaller card) */}
      {path && path.steps.length > 0 && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Assessment Path</h2>
            <span className="text-sm text-gray-500">
              {path.progress.completed} / {path.progress.total} completed
            </span>
          </div>
          <div className="card-body">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-primary-600 transition-all duration-500"
                style={{
                  width: `${
                    path.progress.total > 0
                      ? (path.progress.completed / path.progress.total) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
            {nextStepData?.nextStep && !nextStepData.pathComplete && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Up next: {nextStepData.nextStep.objective.objectiveText}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 capitalize">
                    {nextStepData.nextStep.objective.difficulty}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/assessments/${nextStepData.nextStep!.objective.id}/run`)}
                  className="btn-primary text-sm py-1.5 px-4"
                >
                  Start
                </button>
              </div>
            )}
            {nextStepData?.pathComplete && (
              <p className="text-sm text-green-700 text-center">
                Path complete — all assessments finished.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SECTION D — NEXT BEST STEP
// ============================================

function NextBestStepCard({
  hasAnySessions,
  readiness,
}: {
  hasAnySessions: boolean;
  readiness: ReadinessRoute;
}) {
  // If user has never practiced, override readiness with first-time message
  const message = !hasAnySessions
    ? "Start your first practice session to build your skills."
    : readiness.sublabel;
  const cta = !hasAnySessions
    ? { label: 'Start Practice', to: '/practice' }
    : { label: readiness.label, to: readiness.to };
  const icon = !hasAnySessions ? '🎙️' : readiness.to === '/marketplace' ? '🚀' : '📈';

  return (
    <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
      <div className="card-body flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <span className="text-3xl flex-shrink-0">{icon}</span>
          <div>
            <h3 className="font-semibold text-gray-900">Next Best Step</h3>
            <p className="text-sm text-gray-700 mt-1">{message}</p>
          </div>
        </div>
        <Link
          to={cta.to}
          className="btn-primary whitespace-nowrap text-sm"
        >
          {cta.label}
        </Link>
      </div>
    </div>
  );
}

// ============================================
// SECTION B — MARKETPLACE READINESS
// ============================================

const UNAVAILABLE = 'Unavailable';

export function MarketplaceReadinessCard({
  cardRef,
  profileKnown,
  profileUnavailable = false,
  profileError,
  onRetryProfile,
  authorityUnavailable = false,
  assessmentStatsUnavailable = false,
  unavailableError,
  onRetry,
  authority,
  assessmentStats,
  hasPassedAssessment,
  meetsAuthorityThreshold,
  hasProfileBasics,
  hasFeaturedClip,
  hasPublicProfile,
  readiness,
}: {
  cardRef?: (node: HTMLDivElement | null) => void;
  /** false until the lazily read public-profile settings arrive. */
  profileKnown: boolean;
  /**
   * The public-profile settings read FAILED: the profile rows say
   * "Unavailable" with Retry — never "Incomplete" / "None yet" / "Not published".
   */
  profileUnavailable?: boolean;
  profileError?: unknown;
  onRetryProfile?: () => void;
  /** The level/score could not be read: say so — never "Novice" / "Not measured yet". */
  authorityUnavailable?: boolean;
  /** Test results could not be read: never "None yet". */
  assessmentStatsUnavailable?: boolean;
  unavailableError?: unknown;
  onRetry?: () => void;
  authority?: GamificationOverview['authority'] | null;
  assessmentStats?: UserSessionStats | null;
  hasPassedAssessment: boolean;
  meetsAuthorityThreshold: boolean;
  hasProfileBasics: boolean;
  hasFeaturedClip: boolean;
  hasPublicProfile: boolean;
  readiness: ReadinessRoute;
}) {
  const level = authority?.currentLevel ?? 'novice';
  const colors = LEVEL_COLORS[level];
  // null = nothing measured yet: no progress, and shown as such, never as 0.
  const score = authority?.overallScoreAvg ?? null;
  const progressPercent = score === null ? 0 : Math.min(100, (score / MARKETPLACE_AUTHORITY_THRESHOLD) * 100);

  const checklist = [
    {
      label: `Performance score ${MARKETPLACE_AUTHORITY_THRESHOLD}+`,
      done: meetsAuthorityThreshold,
      detail: authorityUnavailable ? UNAVAILABLE : score === null ? 'Not measured yet' : `Currently ${score.toFixed(0)}`,
    },
    // RC19: while skill practice cannot be scored (the server says so), a
    // pass is impossible — the step is not listed as something to achieve.
    ...(assessmentStats?.scoringAvailable === false
      ? []
      : [
          {
            label: 'Passed at least one skill practice run',
            done: hasPassedAssessment,
            detail: assessmentStatsUnavailable
              ? UNAVAILABLE
              : hasPassedAssessment
                ? `${assessmentStats?.passedSessions ?? 0} passed`
                : 'None yet',
          },
        ]),
    {
      label: 'Profile name and headline set',
      done: hasProfileBasics,
      detail: profileUnavailable ? UNAVAILABLE : !profileKnown ? CHECKING : hasProfileBasics ? 'Complete' : 'Incomplete',
    },
    {
      label: 'Featured example added',
      done: hasFeaturedClip,
      detail: profileUnavailable ? UNAVAILABLE : !profileKnown ? CHECKING : hasFeaturedClip ? 'Added' : 'None yet',
    },
    {
      label: 'Profile published',
      done: hasPublicProfile,
      detail: profileUnavailable ? UNAVAILABLE : !profileKnown ? CHECKING : hasPublicProfile ? 'Live' : 'Not published',
    },
  ];

  const completedCount = checklist.filter((c) => c.done).length;
  const allDone = completedCount === checklist.length;

  return (
    <div className="card" ref={cardRef}>
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrophyIcon className="w-5 h-5 text-primary-600" />
          <h2 className="font-semibold text-gray-900">Ready to Be Discovered</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{completedCount}/{checklist.length}</span>
          {authorityUnavailable ? (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600" data-testid="level-unavailable">
              Level unavailable
            </span>
          ) : authority ? (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
              {authority.currentLevelLabel}
            </span>
          ) : null}
        </div>
      </div>
      <div className="card-body space-y-5">
        {(authorityUnavailable || assessmentStatsUnavailable) && onRetry && (
          <LoadErrorNotice
            error={unavailableError}
            onRetry={onRetry}
            failedMessage={
              authorityUnavailable
                ? 'Your level and progress are unavailable right now.'
                : 'Your skill practice results are unavailable right now.'
            }
            testIdPrefix="dashboard-readiness"
          />
        )}
        {profileUnavailable && onRetryProfile && (
          <LoadErrorNotice
            error={profileError}
            onRetry={onRetryProfile}
            failedMessage="Your profile status is unavailable right now."
            testIdPrefix="dashboard-profile"
          />
        )}
        {/* Progress toward threshold */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Performance Score</span>
            <span className="font-medium text-gray-900">
              {authorityUnavailable || score === null ? '—' : score.toFixed(0)} / {MARKETPLACE_AUTHORITY_THRESHOLD}
            </span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${colors.gradient} transition-all duration-500`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Checklist */}
        <div className="space-y-3">
          {checklist.map((item, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {item.done ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                ) : (
                  <EmptyCircleIcon className="w-5 h-5 text-gray-300" />
                )}
                <span className={`text-sm ${item.done ? 'text-gray-900' : 'text-gray-500'}`}>
                  {item.label}
                </span>
              </div>
              <span className="text-xs text-gray-500">{item.detail}</span>
            </div>
          ))}
        </div>

        {/* CTA — uses same readiness routing as top-level */}
        <div className="pt-2">
          <Link
            to={readiness.to}
            className={`${allDone ? 'btn-primary' : 'btn-secondary'} w-full text-center block`}
          >
            {readiness.label}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SECTION C — RECENT PERFORMANCE
// ============================================

function RecentPerformanceCard({
  gamification,
  recentSessions,
  hasAnySessions,
}: {
  gamification?: GamificationOverview | null;
  recentSessions?: Array<{
    id: string;
    type: string;
    scores: { overall: number; clarity: number; confidence: number; pace: number; energy: number };
    durationSeconds: number | null;
    createdAt: string;
  }>;
  hasAnySessions: boolean;
}) {
  const stats = gamification?.stats;

  if (!hasAnySessions) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-gray-900">Recent Performance</h2>
        </div>
        <div className="card-body text-center py-10">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <MicIcon className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="font-medium text-gray-900 mb-1">No sessions yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Start your first practice session to see your performance here.
          </p>
          <Link to="/practice" className="btn-primary">
            Start Practice
          </Link>
        </div>
      </div>
    );
  }

  const sessions = recentSessions ?? [];
  const latestSession = sessions[0];

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Recent Performance</h2>
        <Link to="/progress" className="text-sm text-primary-600 hover:text-primary-700">
          View All
        </Link>
      </div>
      <div className="card-body space-y-5">
        {/* Summary stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {typeof stats?.totalSessions === 'number' ? stats.totalSessions : '—'}
            </div>
            <div className="text-xs text-gray-500">Sessions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {typeof stats?.avgScore === 'number' ? stats.avgScore.toFixed(0) : '—'}
            </div>
            <div className="text-xs text-gray-500">Avg Score</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{typeof stats?.currentStreak === 'number' ? stats.currentStreak : '—'}</div>
            <div className="text-xs text-gray-500">Streak</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{typeof stats?.longestStreak === 'number' ? stats.longestStreak : '—'}</div>
            <div className="text-xs text-gray-500">Best Streak</div>
          </div>
        </div>

        {/* Latest session scores */}
        {latestSession && (
          <div className="pt-4 border-t">
            <p className="text-xs text-gray-500 mb-3">Latest Session</p>
            <div className="grid grid-cols-4 gap-3">
              <ScoreBar label="Clarity" value={latestSession.scores.clarity} />
              <ScoreBar label="Confidence" value={latestSession.scores.confidence} />
              <ScoreBar label="Pace" value={latestSession.scores.pace} />
              <ScoreBar label="Energy" value={latestSession.scores.energy} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="pt-4 border-t flex flex-wrap gap-3">
          <Link to="/practice" className="btn-primary text-sm py-2 px-4">
            Practice Again
          </Link>
          <Link to="/practice?tab=feedback" className="btn-secondary text-sm py-2 px-4">
            Get Feedback
          </Link>
          <Link to="/skills" className="btn-secondary text-sm py-2 px-4">
            Skill practice
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function ScoreBar({ label, value }: { label: string; value: number }) {
  const percent = Math.min(100, Math.max(0, value));
  const color =
    percent >= 80 ? 'bg-green-500' : percent >= 60 ? 'bg-yellow-500' : 'bg-orange-500';

  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{Math.round(percent)}</span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

// ============================================
// ICONS
// ============================================

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}

function MarketplaceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2M3.26 8.572A2 2 0 015.251 7h13.498a2 2 0 011.991 1.572l.927 6.497A2 2 0 0119.678 17H4.322a2 2 0 01-1.989-2.231l.927-6.197z" />
    </svg>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function EmptyCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <circle cx="12" cy="12" r="9" strokeWidth={2} />
    </svg>
  );
}
